/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
 * the European Commission - subsequent versions of the EUPL (the "Licence");
 * You may not use this work except in compliance with the Licence.
 * You may obtain a copy of the Licence at:
 *
 *   https://joinup.ec.europa.eu/software/page/eupl
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the Licence is distributed on an "AS IS" basis,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the Licence for the specific language governing permissions and
 * limitations under the Licence. */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useState } from "react";
import { useIntl } from "react-intl";
import {
  isModalityAllowed,
  isStopPlaceTypeValid,
  ModalityAllowance,
  StopNameAndTypeFields,
  useHiddenStopTypes,
} from "../../Shared/StopNameAndType";

interface Props {
  open: boolean;
  onConfirm: (name: string, stopType: string) => void;
  onCancel: () => void;
  /** The new stop's location permissions; illegal types are disabled (F-18). */
  allowance?: ModalityAllowance | null;
}

export const NewStopWizard = ({
  open,
  onConfirm,
  onCancel,
  allowance = null,
}: Props) => {
  const { formatMessage } = useIntl();
  const [name, setName] = useState("");
  const [stopType, setStopType] = useState("");
  const hiddenStopTypes = useHiddenStopTypes();

  const canConfirm =
    name.trim().length > 0 &&
    isStopPlaceTypeValid(stopType, hiddenStopTypes) &&
    isModalityAllowed(allowance, stopType, null);

  const handleConfirm = () => {
    onConfirm(name.trim(), stopType);
  };

  const handleCancel = () => {
    setName("");
    setStopType("");
    onCancel();
  };

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={(_, reason) => {
        // Escape routes through the same cleanup as Cancel. Backdrop clicks are
        // deliberately ignored so a stray click cannot abandon a placed stop.
        if (reason === "escapeKeyDown") handleCancel();
      }}
    >
      <DialogTitle>
        {formatMessage({ id: "new_stop_wizard_title" })}
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pt: "16px !important",
        }}
      >
        <StopNameAndTypeFields
          name={name}
          stopType={stopType}
          onNameChange={setName}
          onStopTypeChange={setStopType}
          autoFocus
          allowance={allowance}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="inherit">
          {formatMessage({ id: "cancel" })}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canConfirm}
        >
          {formatMessage({ id: "new_stop_wizard_confirm" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
